import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/business-card.css';

const BusinessCard = () => {
  // Preload video/gif on mount so they're ready instantly
  useEffect(() => {
    // Preload the video
    const video = document.createElement('video');
    video.src = '/impressive.mp4';
    video.preload = 'auto';

    // Preload the gif
    const img = new Image();
    img.src = '/ezgif-5ada9ccd754292bd.gif';
  }, []);

  // Set meta tags for /card page unfurling
  useEffect(() => {
    document.title = "KOMOK Business Card";

    // Update meta tags
    const setMeta = (property, content, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let meta = document.querySelector(`meta[${attr}="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setMeta('og:title', "KOMOK Business Card");
    setMeta('og:description', "Here's a card for serious business with serious news, seriously.");
    setMeta('og:url', "https://komok.org/card");
    setMeta('twitter:title', "KOMOK Business Card");
    setMeta('twitter:description', "Here's a card for serious business with serious news, seriously.");
    setMeta('description', "Here's a card for serious business with serious news, seriously.", true);

    return () => {
      // Reset on unmount
      document.title = "PARTY III";
    };
  }, []);

  const [stage, setStage] = useState(-1); // -1: question, 0: intro, 1: card revealing, 2: monologue, 3: interactive
  const [introVisible, setIntroVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const [currentMonologueLine, setCurrentMonologueLine] = useState(0);
  const [introFading, setIntroFading] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isGlitching, setIsGlitching] = useState(false);
  const [showSweat, setShowSweat] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    attending: 'yes'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mood, setMood] = useState(0); // -1 to 1, negative = no/angry, positive = yes/happy
  const [hoveringYes, setHoveringYes] = useState(false);
  const [holdingButton, setHoldingButton] = useState(null); // 'yes' | 'no' | null
  const [holdProgress, setHoldProgress] = useState(0); // 0 to 1
  const holdProgressRef = useRef(0); // Ref to track progress for cancellation check
  const yesBtnRef = useRef(null);
  const noBtnRef = useRef(null);
  const holdIntervalRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches;

  // Track mouse proximity to Yes/No buttons
  const handleMouseMove = useCallback((e) => {
    if (stage !== -1) return;
    if (!yesBtnRef.current || !noBtnRef.current) return;

    const yesRect = yesBtnRef.current.getBoundingClientRect();
    const noRect = noBtnRef.current.getBoundingClientRect();

    // Calculate center of each button
    const yesCenterX = yesRect.left + yesRect.width / 2;
    const yesCenterY = yesRect.top + yesRect.height / 2;
    const noCenterX = noRect.left + noRect.width / 2;
    const noCenterY = noRect.top + noRect.height / 2;

    // Calculate distance from mouse to each button center
    const distToYes = Math.sqrt(
      Math.pow(e.clientX - yesCenterX, 2) + Math.pow(e.clientY - yesCenterY, 2)
    );
    const distToNo = Math.sqrt(
      Math.pow(e.clientX - noCenterX, 2) + Math.pow(e.clientY - noCenterY, 2)
    );

    // Normalize: closer to Yes = positive mood, closer to No = negative mood
    const maxDist = 400; // Max distance for full effect
    const yesInfluence = Math.max(0, 1 - distToYes / maxDist);
    const noInfluence = Math.max(0, 1 - distToNo / maxDist);

    // Mood ranges from -1 (close to No) to 1 (close to Yes)
    const newMood = yesInfluence - noInfluence;
    setMood(newMood);
  }, [stage]);

  useEffect(() => {
    if (stage === -1) {
      // Desktop: mouse move for proximity effect
      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [stage, handleMouseMove]);

  const monologueLines = [
    <>Look at that subtle <span className="highlight-offwhite">off-white</span> coloring.</>,
    <>The tasteful <span className="highlight-bold">thickness</span> of it.</>,
    <>Oh my <span className="highlight-italic">God</span>.</>,
    <>It <span className="highlight-underline">even</span> has a watermark.</>
  ];

  useEffect(() => {
    if (stage === 0) {
      // Fade out the mood color gradually during intro
      const fadeDuration = 4000; // Fade over 4 seconds
      const fadeInterval = 50;
      const fadeDecrement = (1 / (fadeDuration / fadeInterval));

      const moodFadeTimer = setInterval(() => {
        setMood(prev => {
          const newMood = prev > 0
            ? Math.max(0, prev - fadeDecrement)
            : Math.min(0, prev + fadeDecrement);
          if (Math.abs(newMood) < 0.01) {
            clearInterval(moodFadeTimer);
            return 0;
          }
          return newMood;
        });
      }, fadeInterval);

      // Start glitch first (before card reveals)
      const glitchTimer = setTimeout(() => {
        setIsGlitching(true);
      }, 7000); // Start glitch after third line appears (5.5s + 1.5s)

      // After glitching for a bit, fade intro and reveal card
      const fadeAndRevealTimer = setTimeout(() => {
        setIntroFading(true);
        setCardVisible(true);
      }, 8500); // Glitch for 1.5s before card starts revealing

      // Stop glitch and transition to stage 1
      const glitchEndTimer = setTimeout(() => {
        setIsGlitching(false);
        setIntroVisible(false);
        setStage(1);
      }, 9500); // Total glitch duration ~2.5s

      return () => {
        clearInterval(moodFadeTimer);
        clearTimeout(glitchTimer);
        clearTimeout(fadeAndRevealTimer);
        clearTimeout(glitchEndTimer);
      };
    } else if (stage === 1) {
      // Start monologue quickly after card reveals
      const stageTimer = setTimeout(() => {
        setStage(2);
      }, 400);

      return () => {
        clearTimeout(stageTimer);
      };
    } else if (stage === 2) {
      // Monologue sequence - erratic, building intensity
      if (currentMonologueLine < monologueLines.length) {
        // Vary timing like racing thoughts: slower start, then faster, then "Oh my God" snaps in
        const delays = [1400, 1200, 600, 900]; // Each line's delay before it appears
        const timer = setTimeout(() => {
          setCurrentMonologueLine(prev => prev + 1);
        }, delays[currentMonologueLine]);
        return () => clearTimeout(timer);
      } else {
        const timer = setTimeout(() => {
          setStage(3);
        }, 1500);
        return () => clearTimeout(timer);
      }
    } else if (stage === 3) {
      const sweatTimer = setTimeout(() => {
        if (!isFlipped) {
          setShowSweat(true);
        }
      }, 10000);
      return () => clearTimeout(sweatTimer);
    }
  }, [stage, currentMonologueLine, isFlipped]);

  const handleFlip = () => {
    if (stage < 2) return; // Allow flip during monologue (stage 2+)
    setIsFlipped(!isFlipped);
    setShowSweat(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.target;
    const formDataObj = new FormData(form);

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formDataObj).toString()
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error("Form submission error:", error);
      alert("There was an error submitting your RSVP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBusinessClick = (clickedYes) => {
    // Clicks are now handled by hold completion on both mobile and desktop
    // This is a fallback that shouldn't normally trigger
    return;
  };

  // Hold-to-select handlers (works on both mobile and desktop)
  const startHold = (button) => {
    setHoldingButton(button);
    setHoldProgress(0);
    holdProgressRef.current = 0;
    // Reset mood to 0 so color builds from nothing
    setMood(0);

    const holdDuration = 6000; // 6 seconds
    const intervalTime = 30; // Update every 30ms for smoother animation
    const increment = intervalTime / holdDuration;

    holdIntervalRef.current = setInterval(() => {
      setHoldProgress(prev => {
        const newProgress = prev + increment;
        holdProgressRef.current = newProgress; // Update ref for cancellation check
        // Mood builds linearly throughout the hold
        // This controls the ambient glow color intensity
        const moodValue = newProgress;
        setMood(button === 'yes' ? moodValue : -moodValue);

        if (newProgress >= 1) {
          // Hold complete - trigger selection
          clearInterval(holdIntervalRef.current);
          holdProgressRef.current = 0; // Reset ref
          setMood(button === 'yes' ? 1 : -1);
          setStage(0);
          setIntroVisible(true);
          return 1;
        }
        return newProgress;
      });
    }, intervalTime);
  };

  const endHold = () => {
    // Don't cancel if we're past 60% progress (during crazy effects phase)
    // The shake can cause false mouseLeave/touchEnd events
    if (holdProgressRef.current > 0.6) {
      return;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
    }
    holdProgressRef.current = 0;
    setHoldingButton(null);
    setHoldProgress(0);
    setMood(0);
  };

  // Calculate ambient light color based on mood
  const getAmbientStyle = () => {
    // Only show colored ambient during question (-1) and intro (0) stages
    if (stage > 0) return {};

    // When holding, use holdProgress directly (0 to 1 over 6 seconds)
    // When not holding, use mood from proximity
    const intensity = holdingButton ? holdProgress : Math.abs(mood);
    if (intensity === 0) return {};

    // Determine color direction
    const isPositive = holdingButton ? holdingButton === 'yes' : mood > 0;

    // Tight, bright ellipse that grows and intensifies
    const glowStrength = intensity * 0.8; // 0 -> 0.8
    const size = 30 + intensity * 40; // 30% -> 70%

    if (isPositive) {
      // Green ellipse - starts dim, gets bright and saturated
      const green = Math.round(100 + intensity * 155); // 100 -> 255
      return {
        background: `radial-gradient(ellipse ${size}% ${size * 0.8}% at 50% 50%,
          rgba(0, ${green}, 0, ${glowStrength}) 0%,
          rgba(0, ${Math.round(green * 0.7)}, 0, ${glowStrength * 0.4}) 50%,
          transparent 100%)`
      };
    } else {
      // Red ellipse - starts dim, gets bright and saturated
      const red = Math.round(100 + intensity * 155); // 100 -> 255
      return {
        background: `radial-gradient(ellipse ${size}% ${size * 0.8}% at 50% 50%,
          rgba(${red}, 0, 0, ${glowStrength}) 0%,
          rgba(${Math.round(red * 0.7)}, 0, 0, ${glowStrength * 0.4}) 50%,
          transparent 100%)`
      };
    }
  };

  // Get crazy scene styles based on hold progress
  const getCrazySceneStyle = () => {
    if (!holdingButton || holdProgress === 0) return {};

    const isYes = holdingButton === 'yes';

    // First 4 seconds (0-0.67): Just smooth color darkening, NO shake or crazy effects
    // Last 2 seconds (0.67-1.0): Shake, saturation, contrast, blur kick in

    if (holdProgress < 0.67) {
      // First 4 seconds: No crazy effects at all, just return empty
      // The color transition is handled by the CSS classes and ambient light
      return {};
    }

    // Last 2 seconds: crazy effects ramp up
    const lateProgress = (holdProgress - 0.67) / 0.33; // 0 to 1 in last 2 sec
    const crazyIntensity = lateProgress * lateProgress; // Quadratic ramp

    // Screen shake starts and intensifies
    const shakeAmount = crazyIntensity * 15;
    const shakeX = Math.sin(Date.now() / 35) * shakeAmount;
    const shakeY = Math.cos(Date.now() / 35) * shakeAmount;

    // Hue rotation for psycho effect
    const hueRotate = isYes ? crazyIntensity * 50 : crazyIntensity * -50;

    // Saturation and contrast ramp up
    const saturate = 1 + crazyIntensity * 2.5;
    const contrast = 1 + crazyIntensity * 0.8;

    // Blur only in final moments
    const blur = crazyIntensity > 0.7 ? (crazyIntensity - 0.7) * 6 : 0;

    return {
      transform: `translate(${shakeX}px, ${shakeY}px)`,
      filter: `hue-rotate(${hueRotate}deg) saturate(${saturate}) contrast(${contrast}) blur(${blur}px)`,
      transition: 'none'
    };
  };

  // Get background style that transitions during hold
  const getBackgroundStyle = () => {
    // After hold completes (intro stage), fade to dark calm black
    if (stage >= 0) {
      return {
        background: 'linear-gradient(180deg, #0a0a0a 0%, #050505 100%)',
        transition: 'background 1s ease-out'
      };
    }

    if (!holdingButton || holdProgress === 0) return {};

    const isYes = holdingButton === 'yes';

    // First 4 seconds (0-0.67): background transitions from dark to colored
    // Last 2 seconds (0.67-1): stays at max color, crazy effects take over
    const colorProgress = Math.min(holdProgress / 0.67, 1);

    if (isYes) {
      // Transition from #1a1a1a to dark green
      const r = Math.round(26 - colorProgress * 10); // 26 -> 16
      const g = Math.round(26 + colorProgress * 30); // 26 -> 56
      const b = Math.round(26 - colorProgress * 10); // 26 -> 16
      return {
        background: `linear-gradient(180deg, rgb(${r}, ${g}, ${b}) 0%, rgb(${Math.round(r*0.4)}, ${Math.round(g*0.6)}, ${Math.round(b*0.4)}) 100%)`
      };
    } else {
      // Transition from #1a1a1a to dark red
      const r = Math.round(26 + colorProgress * 30); // 26 -> 56
      const g = Math.round(26 - colorProgress * 10); // 26 -> 16
      const b = Math.round(26 - colorProgress * 10); // 26 -> 16
      return {
        background: `linear-gradient(180deg, rgb(${r}, ${g}, ${b}) 0%, rgb(${Math.round(r*0.6)}, ${Math.round(g*0.4)}, ${Math.round(b*0.4)}) 100%)`
      };
    }
  };

  // Combine background and crazy styles
  const getSceneStyle = () => {
    const bgStyle = getBackgroundStyle();
    // No crazy effects after hold completes (stage >= 0)
    if (stage >= 0) {
      return bgStyle;
    }
    const crazyStyle = getCrazySceneStyle();
    return { ...bgStyle, ...crazyStyle };
  };

  return (
    <div
      className={`card-scene ${stage >= 2 ? 'interactive' : ''} ${hoveringYes ? 'yes-hover' : ''} ${holdingButton ? 'holding-' + holdingButton : ''}`}
      onClick={stage >= 2 ? handleFlip : undefined}
      style={getSceneStyle()}
    >
      {/* Initial question */}
      {stage === -1 && (
        <div className={`business-question ${mood < -0.3 ? 'angry-proximity' : ''} ${mood > 0.3 ? 'happy-proximity' : ''}`}>
          <div className="question-text">
            Do you like <span className={`wavy-text ${hoveringYes ? 'excited' : ''}`}>
              {'business'.split('').map((letter, i) => (
                <span key={i} className="wavy-letter" style={{ animationDelay: `${i * 0.1}s` }}>{letter}</span>
              ))}
            </span>?
          </div>
          <div className="question-buttons">
            <button
              ref={yesBtnRef}
              className={`question-btn yes-btn ${holdingButton === 'yes' ? 'holding' : ''}`}
              onMouseEnter={() => setHoveringYes(true)}
              onMouseLeave={() => { setHoveringYes(false); endHold(); }}
              onMouseDown={() => startHold('yes')}
              onMouseUp={endHold}
              onTouchStart={() => startHold('yes')}
              onTouchEnd={endHold}
              onTouchCancel={endHold}
              style={holdingButton === 'yes' ? { '--hold-progress': holdProgress } : {}}
            >Yes</button>
            <button
              ref={noBtnRef}
              className={`question-btn no-btn ${holdingButton === 'no' ? 'holding' : ''}`}
              onMouseLeave={endHold}
              onMouseDown={() => startHold('no')}
              onMouseUp={endHold}
              onTouchStart={() => startHold('no')}
              onTouchEnd={endHold}
              onTouchCancel={endHold}
              style={holdingButton === 'no' ? { '--hold-progress': holdProgress } : {}}
            >No</button>
          </div>
          <div
            className={`hold-hint ${holdingButton ? 'counting' : ''}`}
            style={holdingButton ? (() => {
              const inCrazyPhase = holdProgress > 0.67;
              const crazyProgress = inCrazyPhase ? (holdProgress - 0.67) / 0.33 : 0;
              const colorProgress = holdProgress / 0.67; // 0 to 1 over first 4 sec, then stays at 1

              return {
                // Scale: subtle in first 4 sec, dramatic in last 2
                transform: `scale(${1 + (inCrazyPhase ? crazyProgress * 1.5 : colorProgress * 0.1)})`,
                // Color: gradual tint in first 4 sec, intense glow in last 2
                color: holdingButton === 'yes'
                  ? `rgba(${100 + (inCrazyPhase ? crazyProgress * 155 : colorProgress * 50)}, ${200 + (inCrazyPhase ? crazyProgress * 55 : colorProgress * 30)}, ${100 + colorProgress * 50}, ${0.5 + (inCrazyPhase ? crazyProgress * 0.5 : colorProgress * 0.2)})`
                  : `rgba(${200 + (inCrazyPhase ? crazyProgress * 55 : colorProgress * 30)}, ${100 - (inCrazyPhase ? crazyProgress * 50 : 0)}, ${100 - (inCrazyPhase ? crazyProgress * 50 : 0)}, ${0.5 + (inCrazyPhase ? crazyProgress * 0.5 : colorProgress * 0.2)})`,
                // Glow only in last 2 seconds
                textShadow: inCrazyPhase ? `0 0 ${crazyProgress * 30}px currentColor` : 'none'
              };
            })() : {}}
          >
            {holdingButton ? (
              // Countdown message while holding (6 seconds)
              Math.ceil((1 - holdProgress) * 6) > 0
                ? `${Math.ceil((1 - holdProgress) * 6)}...`
                : '!'
            ) : (
              'Hold to select'
            )}
          </div>
        </div>
      )}

      {/* Ambient lighting effect */}
      <div className="ambient-light" style={getAmbientStyle()}></div>

      {/* Intro text - centered, fades out after card appears */}
      {introVisible && (
        <div className={`intro-text ${introFading ? 'fading' : ''}`}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <video
              className={`intro-video ${isGlitching ? 'fading-out' : ''}`}
              autoPlay
              muted
              playsInline
              src="/impressive.mp4"
              onError={(e) => {
                // Fallback to gif if mp4 fails to load
                e.target.style.display = 'none';
                e.target.parentElement.querySelector('.intro-gif').style.display = 'block';
              }}
              ref={(el) => {
                if (!el) return;
                // Detect low power mode: video loads but won't autoplay
                const fallbackToGif = () => {
                  el.style.display = 'none';
                  const gif = el.parentElement?.querySelector('.intro-gif');
                  if (gif) gif.style.display = 'block';
                };
                // If video hasn't started playing after 1s, use GIF
                const timeout = setTimeout(() => {
                  if (el.paused || el.readyState < 2) {
                    fallbackToGif();
                  }
                }, 1000);
                el.addEventListener('playing', () => clearTimeout(timeout), { once: true });
                // Also try to play and catch rejection (low power / data saver)
                const playPromise = el.play();
                if (playPromise !== undefined) {
                  playPromise.catch(() => {
                    clearTimeout(timeout);
                    fallbackToGif();
                  });
                }
              }}
            />
            <img
              className={`intro-gif ${isGlitching ? 'fading-out' : ''}`}
              src="/ezgif-5ada9ccd754292bd.gif"
              alt="Impressive, very nice"
              style={{ display: 'none' }}
            />
            <div className="intro-text-overlay">
              <span className="intro-line">Impressive.</span>
              <span className="intro-line delayed-1">Very nice.</span>
              <span
                className={`intro-line delayed-2 ${isGlitching ? 'glitching' : ''}`}
                data-text="Let's see KOMOK's business card."
              >Let's see KOMOK's business card.</span>
            </div>
          </div>
        </div>
      )}

      {/* Card container - always centered */}
      <div className={`card-container ${cardVisible ? 'revealed' : ''} ${isFlipped ? 'flipped' : ''}`}>
        {/* Front of card */}
        <div className="business-card front">
          <div className="card-texture"></div>
          <div className="card-watermark">KOMOK</div>
          <div className="card-content">
            {/* Top row: number left, company right */}
            <div className="card-header">
              <div className="card-number">03.20:6:45PM</div>
              <div className="card-company-block">
                <div className="card-company-name">KOMOK</div>
                <div className="card-company-sub">Dress for Business</div>
              </div>
            </div>

            {/* Center: Name and title */}
            <div className="card-center">
              <div className="card-name">III</div>
              <div className="card-title">BIRTHDAY PARTY</div>
            </div>

            {/* Bottom: Address and details */}
            <div className="card-footer">
              <div className="card-address">VANCOUVER, BC</div>
              <div className="card-details-note">
                <span>Bring Charged Phone, $20, be on time</span>
              </div>
            </div>

            <div className="card-hint">TAP HERE TO RSVP</div>
          </div>
          <div className="emboss-overlay"></div>
        </div>

        {/* Back of card - RSVP form */}
        <div className="business-card back" onClick={handleFlip}>
          <div className="card-texture"></div>
          <div className="card-watermark back-watermark">KOMOK</div>
          <div className="card-content back-content rsvp-content">
            {!isSubmitted ? (
              <>
                <div className="card-company small">RSVP TO III</div>
                <div className="card-rule short"></div>
                <form
                  className="rsvp-form-card"
                  name="card-rsvp-form"
                  method="post"
                  data-netlify="true"
                  onSubmit={handleSubmit}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input type="hidden" name="form-name" value="card-rsvp-form" />
                  <div className="form-field">
                    <input
                      type="text"
                      name="name"
                      placeholder="YOUR NAME"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <select
                      name="attending"
                      value={formData.attending}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="yes">YES</option>
                      <option value="no">NO</option>
                      <option value="maybe">MAYBE</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'SUBMITTING...' : 'SUBMIT RSVP'}
                  </button>
                </form>
                <div className="card-hint back-hint" onClick={handleFlip}>TAP TO FLIP BACK</div>
              </>
            ) : (
              <div className="confirmation">
                <div className="card-company small">CONFIRMED</div>
                <div className="card-rule short"></div>
                <div className="confirmation-message">
                  We look forward to<br />seeing you there.
                </div>
                <div className="confirmation-details">
                  March 20 · 6:45PM
                </div>
              </div>
            )}
          </div>
          <div className="emboss-overlay"></div>
        </div>
      </div>

      {/* Floating monologue thoughts - left and right of card */}
      {stage >= 2 && (
        <div className="monologue-container">
          {monologueLines.map((line, index) => (
            <div
              key={index}
              className={`monologue-line line-${index} ${index < currentMonologueLine ? 'visible' : ''}`}
            >
              {line}
            </div>
          ))}
        </div>
      )}

      {/* Easter egg sweat drop */}
      {/* {showSweat && !isFlipped && stage >= 3 && (
        <div className="sweat-drop"></div>
      )} */}

      {/* Link to original invite */}
      {stage >= 3 && (
        <a href="/" className="original-invite-link">
          Checkout the original invite.
        </a>
      )}
    </div>
  );
};

export default BusinessCard;
